import { DetailedHTMLProps, InputHTMLAttributes, ReactElement, ReactNode, isValidElement } from 'react';
import cn from 'classnames';
import { FontIcon, FontIconName, FontIconSize } from '../../FontIcon';
import { Button, ButtonProps } from '../../Button';
// TODO: use LinkComponent instead of Link
import styles from '../input.module.sass';
import React from 'react';

export interface InputFieldProps extends DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
    label?: string | ReactNode;
    invalid?: boolean;
    errorMessage?: string;
    /**
     * Icon at the end of the input field.
     * If `icon` is a string, icon with default styling is rendered. Alternatively icon component with different
     * color/logic can be passed
     */
    icon?: FontIconName | ReactElement;
    preIcon?: FontIconName | ReactElement;
    /** mobile size; preIconPCSize from $medium */
    preIconSize?: FontIconSize;
    preIconPCSize?: FontIconSize;
    cta?: string | ReactElement;
    buttonLinkProps?: ButtonProps | null;
    // passwordLength?: number;
    ctaLink?: string;
    ctaLinkOnClick?: (e: React.MouseEvent) => void;
    ['data-test-id']?: string;
    qitaf?: boolean;
    handleQitafOnClick?: () => void;
    qitafSuccess?: boolean;
    linkComponent?: any;
    /**
     * Displaying area code in phone input field
     * If `true` show default area code otherwise show area code provided
     */
    areaCode?: boolean | number | string;
}

const InputField = ({
    maxLength = 500,
    label,
    errorMessage,
    icon,
    preIcon,
    preIconSize = 'small',
    preIconPCSize,
    cta,
    ctaLink = '',
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    ctaLinkOnClick = () => {},
    buttonLinkProps,
    // passwordLength = 4,
    invalid = false,
    qitaf = false,
    areaCode,
    className,
    handleQitafOnClick,
    qitafSuccess = false,
    ...props
}: InputFieldProps) => {
    const isFieldWithInput = typeof label === 'string';
    const placeholder = isFieldWithInput ? label : '';
    const DEFAULT_AREA_CODE = '+966';

    const renderIcon = (icon: FontIconName | ReactNode): ReactNode => {
        if (!icon) return null;

        return typeof icon === 'string' ? (
            <span>
                <FontIcon name={icon as FontIconName} md="medium" color="green" />
            </span>
        ) : (
            icon
        );
    };

    let input = <input className={styles.field} placeholder={placeholder} maxLength={maxLength} {...props} />;

    if (areaCode) {
        input = (
            <div className={styles.field}>
                <bdi>{typeof areaCode === 'boolean' ? DEFAULT_AREA_CODE : areaCode}</bdi>
                {input}
            </div>
        );
    }

    return (
        <div data-test-id={'container-' + (props['data-test-id'] ?? '')} className={styles['input-wrapper']}>
            <label
                className={cn(
                    styles.input,
                    {
                        [styles.error]: invalid,
                        [styles.fieldWithoutInput]: !isFieldWithInput,
                        [styles.qitaf]: qitaf,
                        [styles.disabled]: !isFieldWithInput && props.disabled,
                        [styles.simple]: !isFieldWithInput && props.placeholder && !React.isValidElement(cta),
                        [styles.alike]: !isFieldWithInput && props.placeholder && React.isValidElement(cta),
                        [styles.areacode]: areaCode,
                    },
                    className
                )}
            >
                {isFieldWithInput && input}
                <em></em>
                <span className={styles.label} id="search-field" data-test-id="search-field">
                    {label}
                </span>
                {!isFieldWithInput && !label && props.placeholder && (
                    <span className={styles.label}>{props.placeholder}</span>
                )}
                {!isFieldWithInput && React.isValidElement(cta) && props.placeholder && (
                    <span className={styles.field}>{props.value}</span>
                )}
                {(icon || cta || buttonLinkProps) && !qitaf && (
                    <div className={styles.controls}>
                        {icon && renderIcon(icon)}
                        {cta &&
                            (typeof cta === 'string' ? (
                                <a href={ctaLink} className={styles.edit} onClick={(e) => ctaLinkOnClick(e)}>
                                    {cta}
                                </a>
                            ) : React.isValidElement(cta) ? (
                                cta
                            ) : null)}
                        {buttonLinkProps && <Button primary className={styles['additional-button']} {...buttonLinkProps} />}
                    </div>
                )}
                {qitaf && cta && !qitafSuccess && (
                    <div className={styles.controls}>
                        {cta &&
                            (typeof cta === 'string' ? (
                                <a
                                    href={ctaLink}
                                    className={styles.edit}
                                    onClick={(e) => {
                                        if (handleQitafOnClick) {
                                            e.preventDefault();
                                            handleQitafOnClick();
                                        }
                                    }}
                                >
                                    {cta}
                                    <FontIcon name="chevron-left" size="small" md="small" rotate="flip" />
                                </a>
                            ) : React.isValidElement(cta) ? (
                                cta
                            ) : null)}
                    </div>
                )}

                {qitafSuccess && (
                    <FontIcon name="check" color="green" size="small" md="small" className={styles.controls} />
                )}

                {preIcon && (
                    <div className={styles['pre-icon']}>
                        {typeof preIcon === 'string' ? (
                            <FontIcon name={preIcon} color="gray" size={preIconSize} md={preIconPCSize} />
                        ) : (
                            preIcon
                        )}
                    </div>
                )}
            </label>
            {invalid && errorMessage && (
                <span
                    data-test-id={'error-' + (props['data-test-id'] ?? '')}
                    className={cn(styles['help-text'], styles.error)}
                    dangerouslySetInnerHTML={{ __html: errorMessage }}
                />
            )}
        </div>
    );
};

export { InputField };
